import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OpsPayments, RiderCash } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { EmptyState, ErrorState, Loading, StatusPill, useNow, useTitle } from '../../ui/bits';
import { Fig, PageHead, Stat } from '../../ui/viz';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { STATE_ART } from '../../lib/voice.js';
import { count, paymentLabel, peso } from '../../lib/format.js';
import { manilaStamp } from '../reports/period.js';
import { DialogActions, MoneyField, Modal, StaleNote, TextField } from './kit';
import { CashMeter, Dot, Hint, LiveMeta, Quiet, RiderPills } from './money';

import { PAYMENTS_POLL_MS, REMIT_NOTE_MAX, applyRemittance, cashTotals, eventOutcome, ewalletStatus, paymentKeys, providerLabel, recordRemittance, sortRiderCash } from './lib.js';

/**
 * /ops/payments: who holds how much cash against their limit (over-limit riders first, red),
 * "Record cash handed in", e-wallet orders still open, cash handed in and the payment event log.
 * Refreshes every 30 s.
 */
export function OpsPaymentsPage() {
  useTitle('Payments & COD');
  const qc = useQueryClient();
  const now = useNow(1000);
  const q = useQuery({ queryKey: paymentKeys.all, queryFn: () => api.opsPayments(), refetchInterval: PAYMENTS_POLL_MS });
  const [remitFor, setRemitFor] = useState<RiderCash | null>(null);
  const data = q.data;

  return (
    <div className="mn-page">
      <PageHead title="Payments & COD" live={!!data} meta={data ? <LiveMeta updatedAt={q.dataUpdatedAt} now={now} /> : 'Loading'} />

      {q.isError && !data ? (
        <section className="card">
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        </section>
      ) : !data ? (
        <Loading label="Loading payments" rows={4} />
      ) : (
        <>
          <StaleNote show={q.isError} what="payments" />
          <Today data={data} />
          <div className="mn-grid">
            <RiderCashPanel riders={data.riders} onRemit={setRemitFor} />
            <RemittancesPanel rows={data.remittances} />
            <EwalletPanel rows={data.ewalletOpen} />
            <EventsPanel rows={data.events} />
          </div>
        </>
      )}

      <RemitDialog
        rider={remitFor}
        onClose={() => setRemitFor(null)}
        onDone={(rider, remittance) => {
          qc.setQueryData<OpsPayments>(paymentKeys.all, (old) =>
            old ? { ...old, riders: old.riders.map((r) => (r.riderUserId === rider.riderUserId ? applyRemittance(r, remittance) : r)), remittances: [remittance, ...old.remittances] } : old,
          );
          void qc.invalidateQueries({ queryKey: paymentKeys.all });
          void qc.invalidateQueries({ queryKey: ['ops', 'riders'] });
        }}
      />
    </div>
  );
}

const rise = (i: number) => ({ ['--i' as string]: i }) as CSSProperties;

function Today({ data }: { data: OpsPayments }) {
  const t = cashTotals(data.riders);
  const limit = data.riders.reduce((s, r) => s + r.cashLimitCentavos, 0);
  return (
    <section aria-label="Today" className="mn-stats4 dv-rise" style={rise(0)}>
      <Stat label="Cash collected" value={peso(data.today.codCollectedCentavos)} sub="Today · COD" />
      <Stat label="E-wallet paid" value={peso(data.today.ewalletPaidCentavos)} sub="Today · GCash, Maya, card" />
      <Stat label="Refunded" value={peso(data.today.refundsCentavos)} sub="Today" />
      <Stat
        label="Cash with riders"
        tone={t.overLimit ? 'alert' : 'ink'}
        value={peso(t.onHandCentavos)}
        visual={<RiderPills riders={data.riders} />}
        sub={t.overLimit ? <span className="dv-chip bad">{count(t.overLimit)} over limit</span> : limit ? `of ${peso(limit)} limit` : undefined}
      />
    </section>
  );
}

function RiderCashPanel({ riders, onRemit }: { riders: RiderCash[]; onRemit(r: RiderCash): void }) {
  const rows = sortRiderCash(riders);
  const t = cashTotals(riders);
  return (
    <section className="card dv-card mn-12 dv-rise" aria-labelledby="pay-cash-h" style={rise(1)}>
      <div className="dv-card-h">
        <h2 id="pay-cash-h">Rider cash</h2>
        {t.overLimit ? <span className="dv-chip bad">{count(t.overLimit)} over the limit</span> : rows.length ? <span className="dv-chip good">All under limit</span> : null}
        <span className="grow" />
        <span className="dv-sub">On the road</span>
        <Fig text={peso(t.onHandCentavos)} size="sm" animate={false} />
      </div>
      {rows.length === 0 ? (
        <EmptyState art={STATE_ART.noRiders} title="No rider cash yet">
          Riders show up once they collect cash.
        </EmptyState>
      ) : (
        <div className="table-wrap mn-table">
          <table>
            <thead>
              <tr>
                <th scope="col">Rider</th>
                <th scope="col" className="num">
                  On hand
                </th>
                <th scope="col" className="num">
                  Collected
                </th>
                <th scope="col" className="num">
                  Handed in
                </th>
                <th scope="col">Last handed in</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.riderUserId} className={r.overLimit ? 'ops-flag-error' : undefined}>
                  <td>
                    <span className="mn-who">
                      <span className="stack">
                        <span className="row">
                          <Dot tone={r.online ? 'ok' : 'off'} title={r.online ? 'Online' : 'Offline'} />
                          <span className="t">{r.name}</span>
                        </span>
                        <span className="mn-cap">
                          {r.online ? 'Online' : 'Offline'} · {count(r.deliveriesToday)} today
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="num">
                    <div className="mn-oncash">
                      <span className="row">
                        {r.overLimit ? <StatusPill tone="error">Over the limit</StatusPill> : null}
                        <Fig text={peso(r.onHandCentavos)} size="sm" animate={false} />
                      </span>
                      <CashMeter value={r.onHandCentavos} limit={r.cashLimitCentavos} label={`${r.name}: ${peso(r.onHandCentavos)} of a ${peso(r.cashLimitCentavos)} limit`} />
                      <span className={r.overLimit ? 'mn-cap bad' : 'mn-cap'}>
                        {r.overLimit ? `${peso(r.onHandCentavos - r.cashLimitCentavos)} over · ` : ''}limit {peso(r.cashLimitCentavos)}
                      </span>
                    </div>
                  </td>
                  <td className="num">{peso(r.collectedCentavos)}</td>
                  <td className="num">{peso(r.remittedCentavos)}</td>
                  <td>
                    <span className="mn-cap">{r.lastRemittedAt ? manilaStamp(r.lastRemittedAt) : 'Never'}</span>
                  </td>
                  <td className="num">
                    <div className="actions">
                      <button type="button" className={r.overLimit ? 'btn btn-secondary' : 'btn btn-quiet'} disabled={r.onHandCentavos <= 0} onClick={() => onRemit(r)} aria-label={`Record cash handed in by ${r.name}`}>
                        <Icon name="cash" />
                        Hand in
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RemittancesPanel({ rows }: { rows: OpsPayments['remittances'] }) {
  const total = rows.reduce((s, r) => s + r.amountCentavos, 0);
  return (
    <section className="card dv-card mn-5 dv-rise" aria-labelledby="pay-rem-h" style={rise(2)}>
      <div className="dv-card-h">
        <h2 id="pay-rem-h">Cash handed in</h2>
        {rows.length ? <span className="dv-chip">{count(rows.length)}</span> : null}
        <span className="grow" />
        {rows.length ? <Fig text={peso(total)} size="sm" animate={false} /> : null}
      </div>
      {rows.length === 0 ? (
        <Quiet icon={false}>No cash handed in yet.</Quiet>
      ) : (
        <div className="mn-scroll">
          <ul className="mn-rows">
            {rows.map((r) => (
              <li key={r.id}>
                <span className="l">
                  <span className="mn-ic" aria-hidden="true">
                    <Icon name="cash" />
                  </span>
                  <span className="stack">
                    <Fig text={peso(r.amountCentavos)} size="sm" animate={false} />
                    <span className="mn-cap" title={r.note ?? undefined}>
                      {r.note ?? 'No note'}
                    </span>
                  </span>
                </span>
                <span className="stack s4" style={{ textAlign: 'right' }}>
                  <span className="mn-cap">{manilaStamp(r.createdAt)}</span>
                  <span className="mn-cap">{r.receivedByName}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function EwalletPanel({ rows }: { rows: OpsPayments['ewalletOpen'] }) {
  const failed = rows.filter((o) => o.paymentStatus === 'failed').length;
  const total = rows.reduce((s, o) => s + o.totalCentavos, 0);
  return (
    <section className="card dv-card mn-7 dv-rise" aria-labelledby="pay-ew-h" style={rise(3)}>
      <div className="dv-card-h">
        <h2 id="pay-ew-h">Open e-wallet orders</h2>
        <span className="dv-chip">{count(rows.length)}</span>
        {failed ? <span className="dv-chip bad">{count(failed)} failed</span> : null}
        <span className="grow" />
        {rows.length ? <Fig text={peso(total)} size="sm" animate={false} /> : null}
      </div>
      {rows.length === 0 ? (
        <Quiet>Every e-wallet order is paid.</Quiet>
      ) : (
        <ul className="mn-rows">
          {rows.map((o) => {
            const s = ewalletStatus(o.paymentStatus);
            return (
              <li key={o.orderId}>
                <span className="l">
                  <span className="mn-code">{o.code}</span>
                  <StatusPill tone={s.tone}>{s.label}</StatusPill>
                </span>
                <span className="r">
                  <span className="mn-cap">{manilaStamp(o.createdAt)}</span>
                  <Fig text={peso(o.totalCentavos)} size="xs" animate={false} />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function EventsPanel({ rows }: { rows: OpsPayments['events'] }) {
  const flagged = rows.filter((e) => eventOutcome(e.outcome).tone === 'error').length;
  return (
    <section className="card dv-card mn-12 dv-rise" aria-labelledby="pay-ev-h" style={rise(4)}>
      <div className="dv-card-h">
        <h2 id="pay-ev-h">Payment events</h2>
        {flagged ? <span className="dv-chip bad">{count(flagged)} may need money back</span> : null}
        <span className="grow" />
        <Hint text="What GCash, Maya and card payments told us, newest first. Red ones may need money sent back." />
      </div>
      {rows.length === 0 ? (
        <Quiet icon={false}>No payment events yet.</Quiet>
      ) : (
        <div className="table-wrap mn-table">
          <table>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Provider</th>
                <th scope="col">Order</th>
                <th scope="col">Status</th>
                <th scope="col" className="num">
                  Amount
                </th>
                <th scope="col">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const o = eventOutcome(e.outcome);
                return (
                  <tr key={e.id} className={o.tone === 'error' ? 'ops-flag-error' : undefined}>
                    <td>
                      <span className="mn-cap">{manilaStamp(e.createdAt)}</span>
                    </td>
                    <td>{providerLabel(e.provider)}</td>
                    <td className="mn-code">{e.orderRef ?? '—'}</td>
                    <td>{e.status ? paymentLabel(e.status) : '—'}</td>
                    <td className="num">{peso(e.amountCentavos)}</td>
                    <td>
                      <StatusPill tone={o.tone}>{o.label}</StatusPill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RemitDialog({ rider, onClose, onDone }: { rider: RiderCash | null; onClose(): void; onDone(rider: RiderCash, remittance: OpsPayments['remittances'][number]): void }) {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; note?: string }>({});
  const amountRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setAmount(rider ? String(rider.onHandCentavos / 100) : '');
    setNote('');
    setErrors({});
  }, [rider]);

  const save = useMutation({
    mutationFn: (v: { rider: RiderCash; form: { amount: string; note: string } }) => recordRemittance(api, v.rider, v.form),
    onSuccess: (r, v) => {
      if (!r.ok) {
        setErrors(r.errors);
        amountRef.current?.focus();
        return;
      }
      toast(`${peso(r.remittance.amountCentavos)} from ${v.rider.name} recorded.`);
      onDone(v.rider, r.remittance);
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (rider) save.mutate({ rider, form: { amount, note } });
  };

  return (
    <Modal open={rider !== null} labelledBy="pay-remit-title" onClose={onClose}>
      <form className="stack s24" onSubmit={submit} noValidate>
        <div className="stack s4">
          <h2 id="pay-remit-title" className="t-headline">
            Cash handed in by {rider?.name}
          </h2>
          <span className="t-caption muted">Count it first. They hold {peso(rider?.onHandCentavos ?? 0)} right now.</span>
        </div>
        <MoneyField id="pay-remit-amount" label="Amount handed in" value={amount} onChange={setAmount} error={errors.amount} help="All of it, or part if they keep some for change." inputRef={amountRef} />
        <TextField id="pay-remit-note" label="Note (optional)" value={note} onChange={setNote} error={errors.note} maxLength={REMIT_NOTE_MAX} placeholder="Counted at the hub" />
        {save.isError ? (
          <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
            {errorMessage(save.error, "Couldn't record it. Try again.")}
          </p>
        ) : null}
        <DialogActions busy={save.isPending} busyLabel="Recording…" confirm="Record cash" onCancel={onClose} />
      </form>
    </Modal>
  );
}
